import { isCssSize } from 'x1-base/utils';
import type {
    ContentBlockRenderedHeight,
    ContentBlockRenderedPosition,
    ContentBlockRenderedImagePosition,
    HeroCarouselItemValuesBase
} from '~/types';

const BLOCK_HEIGHTS: Record<ContentBlockRenderedHeight, string> = {
    auto: 'auto',
    small: '24em',
    medium: '32em',
    large: '40em'
};

// For a flex-column container: justify-* is the vertical axis, align-* the horizontal.
const BLOCK_TEXT_POSITION_CLASSES: Record<ContentBlockRenderedPosition, string> = {
    'top-left': 'justify-start align-start',
    'top-center': 'justify-start align-center',
    'top-right': 'justify-start align-end',
    'middle-left': 'justify-center align-start',
    'middle-center': 'justify-center align-center',
    'middle-right': 'justify-center align-end',
    'bottom-left': 'justify-end align-start',
    'bottom-center': 'justify-end align-center',
    'bottom-right': 'justify-end align-end'
};

export interface UseContentBlocksUtilsOptions {}
export function useContentBlocksUtils(options: Partial<UseContentBlocksUtilsOptions> = {}) {

    function getBlockHeightStyle(height: ContentBlockRenderedHeight | (string & {})): string {
        const preset = BLOCK_HEIGHTS[height as ContentBlockRenderedHeight];
        return preset ?? (isCssSize(height) ? height : BLOCK_HEIGHTS.auto);
    }

    function getBlockTextPositionClasses(position: ContentBlockRenderedPosition): string {
        return BLOCK_TEXT_POSITION_CLASSES[position] ?? BLOCK_TEXT_POSITION_CLASSES['middle-center'];
    }

    /**
     * Maps physical object-position keywords onto flex classes for a
     * flex-column media container (align-* horizontal, justify-* vertical).
     * An explicit 'unset' applies no alignment classes; missing or logical
     * values ('block-start', 'inline-end', 'start', 'end', ...) fall back
     * to the default alignment.
     */
    function getBlockMediaPositionClasses(position: Undefinable<ContentBlockRenderedImagePosition>): string {
        if (position === 'unset') {
            return '';
        }

        const tokens = position?.split(' ') ?? [];
        const horizontal = tokens.find((token) => token === 'left' || token === 'right');
        const vertical = tokens.find((token) => token === 'top' || token === 'bottom');

        if (!horizontal && !vertical && !tokens.includes('center')) {
            return 'align-center justify-end';
        }

        const alignClass = horizontal === 'left'
            ? 'align-start'
            : horizontal === 'right'
                ? 'align-end'
                : 'align-center';
        const justifyClass = vertical === 'top'
            ? 'justify-start'
            : vertical === 'bottom'
                ? 'justify-end'
                : 'justify-center';

        return `${alignClass} ${justifyClass}`;
    }

    /**
     * Shared bindings for the X/Y/Z-split hero carousel item components.
     *
     * `linkComponent` must be resolved by the calling component
     * (`resolveComponent('NuxtLink')` in its script setup) — Nuxt only rewrites
     * literal resolveComponent calls to '#components' imports inside SFCs, and
     * this file must stay importable from the Nitro server build.
     */
    function getHeroCarouselItemBindings(
        values: () => HeroCarouselItemValuesBase & {
            imagePosition?: ContentBlockRenderedImagePosition;
            altImagePosition?: ContentBlockRenderedImagePosition;
        },
        isMobile: () => boolean,
        linkComponent: ReturnType<typeof resolveComponent>
    ) {
        const buttons = computed(() => values().buttons ?? []);

        // Whole-slide hyperlink only when flagged, a path exists, and at most one
        // button is configured (schema: hyperlink is ignored with more buttons).
        const isBlockLink = computed(() =>
            values().hyperlinkBlock
            && !!values().hyperlinkPath
            && buttons.value.length <= 1
        );

        const rootComponent = computed(() => (isBlockLink.value ? linkComponent : 'div'));
        const rootProps = computed(() => (isBlockLink.value ? { to: values().hyperlinkPath } : {}));

        const textPositionClasses = computed(
            () => getBlockTextPositionClasses(
                isMobile() ? values().textPositionMobile : values().textPosition
            )
        );
        const textAlignClass = computed(() => isMobile() ? 'text-center' : `text-${values().textAlign}`);

        // textBlockTheme 'dark' = dark backdrop -> white text; 'light' = light
        // backdrop -> default dark text. The overlay pairs with the text color.
        const textThemeClass = computed(() => values().textBlockTheme === 'dark' ? 'text-white' : '');
        const textStrokeClass = computed(() => values().textBlockTheme === 'light' ? 'text-stroke-light' : 'text-stroke-dark');
        const buttonTheme = computed(() => values().textBlockTheme === 'light' ? 'light' : 'dark');
        const textOverlayClass = computed(() => {
            if (!values().textBlockBackgroundFill) return '';
            return values().textBlockTheme === 'dark' ? 'text-block-overlay-dark' : 'text-block-overlay-light';
        });
        const textPadClass = computed(() => values().padTextBlock ? 'pa-6' : 'pa-2');
        const mediaPadClass = computed(() => values().padImageBlock ? 'pa-4' : 'pa-0');
        const mediaPositionClasses = computed(() => getBlockMediaPositionClasses(
            isMobile() && values().altImage ? values().altImagePosition : values().imagePosition
        ));

        return {
            buttons,
            isBlockLink,
            rootComponent,
            rootProps,
            textPositionClasses,
            textAlignClass,
            textThemeClass,
            textStrokeClass,
            buttonTheme,
            textOverlayClass,
            textPadClass,
            mediaPadClass,
            mediaPositionClasses
        };
    }

    return {
        getBlockHeightStyle,
        getBlockTextPositionClasses,
        getBlockMediaPositionClasses,
        getHeroCarouselItemBindings
    };
}
